import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

const ManageFieldsDialog = ({ open, onConfirm, onClose, data, fields, title = 'New item' }) => {
  const [tempData, setTempData] = useState(() => ({ ...(data || {}) }));

  useEffect(() => {
    if (open) {
      setTempData({ ...(data || {}) });
    }
  }, [open, data]);

  const handleTextChange = useCallback(
    (name) => (e) => {
      const { value } = e.target;
      setTempData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleAutocompleteChange = useCallback((name) => (e, option) => {
    setTempData((prev) => ({
      ...prev,
      [name]: option == null ? '' : option.value,
    }));
  }, []);

  const isConfirmDisabled = useMemo(() => {
    if (!fields || fields.length === 0) {
      return true;
    }
    return fields.some((field) => {
      if (field.required === false) {
        return false;
      }
      const raw = tempData[field.name];
      if (field.type === 'text') {
        return raw == null || String(raw).trim() === '';
      }
      if (field.type === 'autocomplete') {
        return raw == null || raw === '';
      }
      return false;
    });
  }, [fields, tempData]);

  const dialogTitle = (data && data.name) || title;

  return (
    <div>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            minWidth: '450px',
            minHeight: '200px',
          }}
        >
          {fields.map((field, index) => {
            if (field.type === 'text') {
              return (
                <TextField
                  key={field.name}
                  name={field.name}
                  variant="outlined"
                  size="small"
                  label={field.label}
                  value={tempData[field.name] ?? ''}
                  onChange={handleTextChange(field.name)}
                  disabled={field.disabled}
                  InputProps={{ readOnly: field.readonly }}
                  sx={{ marginTop: index === 0 ? '10px' : undefined }}
                />
              );
            }
            if (field.type === 'autocomplete') {
              const options = field.options || [];
              const selected =
                options.find((opt) => opt.value === tempData[field.name]) ?? null;
              return (
                <Autocomplete
                  key={field.name}
                  disablePortal
                  options={options}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={selected}
                  size="small"
                  onChange={handleAutocompleteChange(field.name)}
                  readOnly={field.readonly}
                  disabled={field.disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={field.label}
                      sx={{ marginTop: index === 0 ? '10px' : undefined }}
                    />
                  )}
                />
              );
            }
            return null;
          })}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(tempData)} disabled={isConfirmDisabled}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ManageFieldsDialog;
